import { clsx } from 'clsx';
import { useId } from 'react';

/**
 * On/off switch on a native checkbox with role="switch". The knob moves and the track prints
 * ON or OFF, so the state reads without color. The whole row is the target (48 px).
 *
 *   <Switch label="Track misses" help="Slower, but the stats get spicy" checked={v} onChange={setV} />
 */
export interface SwitchProps {
  label: string;
  help?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  className?: string;
}

export function Switch({ label, help, checked, onChange, disabled, className }: SwitchProps) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={clsx(
        'flex min-h-12 cursor-pointer items-center justify-between gap-4 py-2',
        disabled && 'cursor-not-allowed opacity-55',
        className,
      )}
    >
      <span className="flex flex-col">
        <span className="font-semibold">{label}</span>
        {help && (
          <span id={`${id}-h`} className="text-sm text-ink/75">
            {help}
          </span>
        )}
      </span>
      <span className="relative inline-flex shrink-0">
        <input
          id={id}
          type="checkbox"
          role="switch"
          aria-checked={checked}
          aria-describedby={help ? `${id}-h` : undefined}
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span
          aria-hidden="true"
          className="flex h-9 w-[4.25rem] items-center rounded-tts border-3 border-ink bg-white px-1.5 font-display text-[11px] font-black italic shadow-[3px_3px_0_0_var(--color-ink)] transition-colors peer-checked:justify-start peer-checked:bg-mint peer-focus-visible:outline-3 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ink"
        >
          <span className={clsx('w-full', checked ? 'pr-6 text-left' : 'pl-6 text-right')}>
            {checked ? 'ON' : 'OFF'}
          </span>
        </span>
        <span
          aria-hidden="true"
          className={clsx(
            'absolute top-[7px] left-[7px] block size-[22px] rounded-chip border-3 border-ink bg-ink transition-transform duration-(--dur-fast)',
            checked && 'translate-x-[30px] bg-white',
          )}
        />
      </span>
    </label>
  );
}
